import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "../layout/Layout";
import SEO from "../Components/SEO/SEO";
import { Button } from "../Components/ui/Button";
import { CheckCircle2, ArrowRight } from "lucide-react";

const founderMessage = {
  name: "Muhammad Shahzaib",
  role: "Founder & CEO, Quantum Spark Technology",
  image: "/team-headshots/shahzaib.png",
  quote:
    "I started Quantum Spark Technology because founders deserve a team that ships — not endless retainers with no exit criteria. We stay hands-on in architecture and quality, keep scope honest, and treat every launch like our reputation depends on it.",
};

const values = [
  "Honest timelines over inflated promises",
  "Code and IP you fully own after handoff",
  "Senior-led architecture on every engagement",
  "Weekly demos and transparent sprint progress",
];

export default function AboutPage() {
  return (
    <Layout>
      <SEO
        title="About Quantum Spark Technology | Software Development Team"
        description="Learn how Quantum Spark Technology designs and builds web, mobile, AI, and blockchain products with a senior-led delivery team."
        path="/about"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#FAFAFB] pt-12 pb-16 md:pt-16 md:pb-20">
        <div className="pointer-events-none absolute top-28 right-0 h-[500px] w-[500px] translate-x-1/4 rounded-full bg-[#6F00FF]/5 blur-[120px]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-[#6F00FF]">About</p>
            <h1 className="mb-6 font-display text-4xl font-extrabold tracking-tight text-[#0B0B16] md:text-6xl">
              Software built for real products,{" "}
              <span className="bg-gradient-to-r from-[#6F00FF] to-[#9333EA] bg-clip-text text-transparent">
                not pitch decks
              </span>
            </h1>
            <p className="text-lg leading-relaxed text-[#5A5A6E]">
              Quantum Spark Technology is a software development studio that helps founders and product teams ship
              websites, mobile apps, AI-assisted features, and blockchain platforms — with clear scope, senior-led
              architecture, and delivery support that continues after launch.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder message */}
      <section className="border-y border-zinc-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-4xl rounded-[2rem] border border-zinc-200 bg-[#FAFAFB] p-8 md:p-12"
          >
            <p className="mb-2 text-sm font-bold uppercase tracking-widest text-[#6F00FF]">A note from our founder</p>
            <blockquote className="mb-8 font-display text-2xl font-medium leading-snug text-[#0B0B16] md:text-3xl">
              &ldquo;{founderMessage.quote}&rdquo;
            </blockquote>
            <div className="flex items-center gap-5">
              <img
                src={founderMessage.image}
                alt={founderMessage.name}
                className="h-16 w-16 rounded-full border-2 border-white object-cover shadow-md"
              />
              <div>
                <p className="font-display text-lg font-bold text-[#0B0B16]">{founderMessage.name}</p>
                <p className="text-sm font-medium text-[#5A5A6E]">{founderMessage.role}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2">
          <article className="space-y-4 text-base leading-relaxed text-[#5A5A6E]">
            <h2 className="font-display text-2xl font-bold text-[#0B0B16]">What we do</h2>
            <p>
              Clients come to us when they need more than a brochure site. Typical engagements include SaaS dashboards,
              e-commerce experiences, NFC and connectivity products, marketing platforms for Web3 communities, and
              consumer apps that need reliable APIs, clean UI, and measurable conversion paths.
            </p>
            <p>
              Our core services cover UI/UX design, website and web app development, mobile apps, AI feature integration,
              blockchain development, and quality assurance. We work in Agile sprints with transparent progress updates,
              and we hand over code you fully own.
            </p>
          </article>

          <article className="space-y-4 text-base leading-relaxed text-[#5A5A6E]">
            <h2 className="font-display text-2xl font-bold text-[#0B0B16]">Who we serve</h2>
            <p>
              We partner with startups and growing companies that need a focused engineering team without the overhead of
              building an entire in-house department overnight. Recent collaboration areas include Korean skincare
              e-commerce, NFC digital identity products, wireless marketing experiences, and IP marketplace platforms
              like MustangIP.
            </p>
            <ul className="space-y-3 pt-2">
              {values.map((value) => (
                <li key={value} className="flex items-start gap-2 text-sm font-medium text-zinc-700">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#6F00FF]" />
                  {value}
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>

      {/* How we work */}
      <section className="border-t border-zinc-200 bg-[#FAFAFB] py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-10 font-display text-3xl font-bold text-[#0B0B16]">How we work</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { step: "01", title: "Discover", desc: "Clarify goals, users, constraints, and success metrics before writing production code." },
              { step: "02", title: "Design", desc: "Wireframes and high-fidelity UI that match the product's brand and conversion goals." },
              { step: "03", title: "Build", desc: "Frontend and backend delivery with reviews, staging demos, and documented handoff." },
              { step: "04", title: "Launch", desc: "QA, deployment help, and a practical post-launch window so issues get fixed quickly." },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-3xl border border-zinc-200 bg-white p-6"
              >
                <p className="mb-2 font-display text-2xl font-black text-[#6F00FF]/30">{item.step}</p>
                <h3 className="mb-2 font-display text-lg font-bold text-[#0B0B16]">{item.title}</h3>
                <p className="text-sm leading-relaxed text-[#5A5A6E]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-6 mb-6 overflow-hidden rounded-[3rem] bg-[#6F00FF] py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-4 font-display text-3xl font-bold text-white md:text-4xl">Let&apos;s build something real</h2>
          <p className="mb-8 text-lg text-white/80">
            Tell us what you&apos;re working on. We&apos;ll respond within 24 hours with next steps for a discovery call.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/#contact">
              <Button className="h-14 rounded-full bg-white px-8 text-base font-bold text-[#6F00FF] hover:bg-zinc-100">
                Start a project <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                variant="outline"
                className="h-14 rounded-full border-white/30 bg-transparent px-8 text-base font-semibold text-white hover:bg-white/10"
              >
                View services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
